import { useNavigate } from "react-router-dom";
import { Heart, Monitor, GraduationCap, ArrowRight, Phone, Mail, MapPin, Shield, Wifi, Clock, ExternalLink, TrendingUp, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  {
    id: "education",
    icon: GraduationCap,
    title: "Education",
    subtitle: "Heart & Stroke CPR Training",
    color: "from-red-500 to-red-700",
    border: "border-red-200",
    items: [
      "Heart & Stroke BLS Provider (CPR-C/AED)",
      "Workplace & Community CPR Programs",
      "Customized group training available",
    ],
  },
  {
    id: "digital",
    icon: Monitor,
    title: "Digital Solutions",
    subtitle: "EMS Clinical Guidelines",
    color: "from-blue-500 to-blue-700",
    border: "border-blue-200",
    items: [
      "Offline-first clinical guideline applications",
      "Interactive drug calculators with volume draw-up",
      "Real-time code timers & intervention tracking",
      "Equipment reference tools (Broselow, RSI checklists)",
      "Custom-built for your service's protocols",
    ],
    hasApp: true,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-4 mb-6 justify-center md:justify-start">
                <svg viewBox="0 0 48 48" className="w-14 h-14" xmlns="http://www.w3.org/2000/svg">
                  <rect width="48" height="48" rx="10" fill="#1a2744"/>
                  <g transform="translate(24,24)">
                    <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9"/>
                    <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(60)"/>
                    <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(-60)"/>
                    <circle cx="0" cy="0" r="7" fill="#1a2744"/>
                    <polyline points="-5,1 -3,1 -1.8,2.5 -0.5,-4.5 1,3 2.2,-0.5 3,1 5,1" fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                </svg>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1a2744]">Bell Health Services</h1>
                  <p className="text-lg text-[#0c8ce9] font-medium">and Technology</p>
                </div>
              </div>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl leading-relaxed">
                Empowering first responders through clinical education and purpose-built digital tools for emergency medical services.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <Button
                  onClick={() => navigate("/login")}
                  size="lg"
                  className="gap-2 bg-[#1a2744] hover:bg-[#253656] text-white px-6 h-12 text-base"
                  data-testid="launch-app-btn"
                >
                  <Monitor className="w-5 h-5" />
                  Launch YEMS App
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                  variant="outline"
                  size="lg"
                  className="gap-2 border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-12 text-base"
                >
                  Our Services
                </Button>
              </div>
            </div>

            {/* Feature highlights */}
            <div className="flex-1 grid grid-cols-2 gap-3 max-w-sm">
              {[
                { icon: Shield, label: "Offline-First", desc: "Works without signal" },
                { icon: Heart, label: "Clinical Tools", desc: "Calculators & timers" },
                { icon: Clock, label: "Real-Time", desc: "Code management" },
                { icon: Wifi, label: "PWA", desc: "Install on any device" },
              ].map((f, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                  <f.icon className="w-6 h-6 mx-auto mb-2 text-[#0c8ce9]" />
                  <div className="text-sm font-semibold text-gray-800">{f.label}</div>
                  <div className="text-xs text-gray-500">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Services */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#1a2744]">Our Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">Bridging the gap between clinical education and technology to support emergency medical professionals in the field.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.map((svc) => (
            <div key={svc.id} className={`rounded-2xl border ${svc.border} bg-white overflow-hidden shadow-sm`} data-testid={`service-${svc.id}`}>
              <div className={`bg-gradient-to-r ${svc.color} px-6 py-5`}>
                <div className="flex items-center gap-3">
                  <svc.icon className="w-7 h-7 text-white" />
                  <div>
                    <h3 className="text-xl font-bold text-white">{svc.title}</h3>
                    <p className="text-white/80 text-sm">{svc.subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {svc.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-gray-600 text-sm">
                      <span className="text-[#0c8ce9] mt-0.5 font-bold">+</span>
                      {item}
                    </li>
                  ))}
                </ul>
                {svc.hasApp && (
                  <div className="space-y-2">
                    <Button
                      onClick={() => navigate("/login")}
                      className="w-full gap-2 bg-[#1a2744] hover:bg-[#253656] text-white"
                      data-testid="service-launch-app"
                    >
                      <Monitor className="w-4 h-4" />
                      Launch YEMS Clinical Guidelines
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => window.location.href = "mailto:sbell.yt@gmail.com?subject=Digital%20Solutions%20Inquiry"}
                      variant="outline"
                      className="w-full gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Mail className="w-4 h-4" />
                      Contact Us
                    </Button>
                  </div>
                )}
                {!svc.hasApp && (
                  <Button
                    onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                    variant="outline"
                    className="w-full gap-2 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <GraduationCap className="w-4 h-4" />
                    Inquire About Training
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="bg-slate-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-[#1a2744]">About Bell Health</h2>
            
            <div className="flex items-center gap-4 mb-8 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
              <svg viewBox="0 0 48 48" className="w-12 h-12 shrink-0" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="10" fill="#1a2744"/>
                <g transform="translate(24,24)">
                  <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9"/>
                  <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(60)"/>
                  <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(-60)"/>
                  <circle cx="0" cy="0" r="7" fill="#1a2744"/>
                  <polyline points="-5,1 -3,1 -1.8,2.5 -0.5,-4.5 1,3 2.2,-0.5 3,1 5,1" fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
              </svg>
              <div>
                <h3 className="text-lg font-bold text-[#1a2744]">Sean Bell</h3>
                <p className="text-sm text-[#0c8ce9]">Founder — Licensed Practical Nurse | Advanced Care Paramedic</p>
              </div>
            </div>

            <div className="space-y-5 text-gray-600 leading-relaxed">
              <p>
                Sean has over 20 years of experience in emergency services and health care across British Columbia and the Yukon. He began his career as a volunteer firefighter in 1996 and transitioned into paramedicine in 2012, working as a Primary Care Paramedic in both the Yukon Territory and Northern British Columbia. In 2018, he completed the Licensed Practical Nursing program at Yukon University, adding hospital-based care to his clinical background. In 2023, he completed his Advanced Care Paramedic diploma and continues to provide frontline care to Yukoners.
              </p>

              <p>
                Working in the Yukon — where connectivity is unreliable and resources are limited — shaped his approach to building practical tools. The YEMS Clinical Guidelines app came from a straightforward need: paramedics should have quick, reliable access to their protocols and clinical tools, whether they have cell service or not.
              </p>

              <p>
                Bell Health Services and Technology builds digital tools and provides clinical education for emergency medical services. Everything we build reflects how people actually work in the field.
              </p>
            </div>

            <div className="mt-8 flex justify-center">
              <div className="bg-white border border-gray-200 rounded-xl p-5 text-center min-w-[200px] shadow-sm">
                <div className="text-3xl font-bold text-[#0c8ce9]">20+</div>
                <div className="text-xs text-gray-500">Years in Health and Emergency Services</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Other Projects */}
      <section id="other-projects" className="bg-slate-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#1a2744]">Our Other Projects</h2>
            <p className="text-gray-500">A few of the other things we're building.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Tradewinds */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1a2744]">Tradewinds</h3>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">AI-Powered Trading Insights</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                Real-time market analytics and AI-assisted decision support for active traders.
              </p>
              <a
                href="https://ai-trader-178.emergent.host/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                data-testid="visit-tradewinds-btn"
              >
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11">
                  Visit Tradewinds
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>

            {/* Bosun's Mate */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
                  <Compass className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1a2744]">Bosun's Mate</h3>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Marine Companion App</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                A practical digital companion for boaters — checklists, references, and tools for life on the water.
              </p>
              <a
                href="https://bosunsmate.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                data-testid="visit-bosunsmate-btn"
              >
                <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white gap-2 h-11">
                  Visit Bosun's Mate
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#1a2744]">Get in Touch</h2>
          <p className="text-gray-500">Interested in our training programs or digital solutions? We'd love to hear from you.</p>
        </div>
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#0c8ce9]" />
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Email</div>
              <a href="mailto:sbell.yt@gmail.com" className="text-sm text-gray-700 hover:text-[#0c8ce9]">sbell.yt@gmail.com</a>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-[#0c8ce9]" />
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Phone</div>
              <a href="tel:+18673362244" className="text-sm text-gray-700 hover:text-[#0c8ce9]">(867) 336-2244</a>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-[#0c8ce9]" />
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Location</div>
              <span className="text-sm text-gray-700">Whitehorse, Yukon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 48 48" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
              <rect width="48" height="48" rx="10" fill="#1a2744"/>
              <g transform="translate(24,24)">
                <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9"/>
                <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(60)"/>
                <rect x="-3.5" y="-14.5" width="7" height="29" rx="1.8" fill="#0c8ce9" transform="rotate(-60)"/>
                <circle cx="0" cy="0" r="7" fill="#1a2744"/>
                <polyline points="-5,1 -3,1 -1.8,2.5 -0.5,-4.5 1,3 2.2,-0.5 3,1 5,1" fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
            </svg>
            <span className="text-sm text-gray-500">Bell Health Services and Technology</span>
          </div>
          <div className="text-xs text-gray-400">
            Whitehorse, Yukon
          </div>
        </div>
      </footer>
    </div>
  );
}
